import { useCallback, useRef, useEffect, useState } from 'react'

const DB_NAME = 'esp32-calendar-images'
const DB_VERSION = 1
const STORE_NAME = 'images'

interface CacheEntry {
  filename: string
  data: ArrayBuffer
  timestamp: number
}

/**
 * Hook for caching images in IndexedDB
 * Provides persistent storage across browser sessions
 */
export function useImageCache() {
  const dbRef = useRef<IDBDatabase | null>(null)
  const [isReady, setIsReady] = useState(false)

  // In-memory URL cache for object URLs
  const urlCache = useRef<Map<string, string>>(new Map())

  // Initialize IndexedDB
  useEffect(() => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      console.error('[ImageCache] Failed to open IndexedDB:', request.error)
    }

    request.onsuccess = () => {
      dbRef.current = request.result
      setIsReady(true)
      console.log('[ImageCache] IndexedDB ready')
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'filename' })
        store.createIndex('timestamp', 'timestamp', { unique: false })
        console.log('[ImageCache] Created object store')
      }
    }

    // Cleanup object URLs on unmount
    return () => {
      urlCache.current.forEach(url => URL.revokeObjectURL(url))
      urlCache.current.clear()
    }
  }, [])

  /**
   * Check if an image is in the cache
   */
  const hasImage = useCallback(async (filename: string): Promise<boolean> => {
    if (!dbRef.current) return false

    return new Promise((resolve) => {
      const transaction = dbRef.current!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(filename)

      request.onsuccess = () => resolve(!!request.result)
      request.onerror = () => resolve(false)
    })
  }, [])

  /**
   * Get an image from the cache as an object URL
   * Returns null if not in cache
   */
  const getImageUrl = useCallback(async (filename: string): Promise<string | null> => {
    // Check in-memory URL cache first
    if (urlCache.current.has(filename)) {
      return urlCache.current.get(filename)!
    }

    if (!dbRef.current) return null

    return new Promise((resolve) => {
      const transaction = dbRef.current!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(filename)

      request.onsuccess = () => {
        const entry = request.result as CacheEntry | undefined
        if (entry) {
          // Create object URL from cached data
          const blob = new Blob([entry.data], { type: 'image/bmp' })
          const url = URL.createObjectURL(blob)
          urlCache.current.set(filename, url)
          resolve(url)
        } else {
          resolve(null)
        }
      }

      request.onerror = () => resolve(null)
    })
  }, [])

  /**
   * Get image as ArrayBuffer from cache
   */
  const getImageData = useCallback(async (filename: string): Promise<ArrayBuffer | null> => {
    if (!dbRef.current) return null

    return new Promise((resolve) => {
      const transaction = dbRef.current!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(filename)

      request.onsuccess = () => {
        const entry = request.result as CacheEntry | undefined
        resolve(entry?.data ?? null)
      }

      request.onerror = () => resolve(null)
    })
  }, [])

  /**
   * Store an image in the cache
   */
  const setImage = useCallback(async (filename: string, data: ArrayBuffer): Promise<void> => {
    if (!dbRef.current) {
      console.warn('[ImageCache] Database not ready')
      return
    }

    const entry: CacheEntry = {
      filename,
      data,
      timestamp: Date.now()
    }

    return new Promise((resolve, reject) => {
      const transaction = dbRef.current!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(entry)

      request.onsuccess = () => {
        console.log(`[ImageCache] Cached: ${filename}`)

        // Create object URL immediately
        const blob = new Blob([data], { type: 'image/bmp' })
        const url = URL.createObjectURL(blob)
        urlCache.current.set(filename, url)

        resolve()
      }

      request.onerror = () => {
        console.error('[ImageCache] Failed to cache:', filename, request.error)
        reject(request.error)
      }
    })
  }, [])

  /**
   * Remove an image from the cache
   */
  const removeImage = useCallback(async (filename: string): Promise<void> => {
    // Revoke object URL if exists
    const url = urlCache.current.get(filename)
    if (url) {
      URL.revokeObjectURL(url)
      urlCache.current.delete(filename)
    }

    if (!dbRef.current) return

    return new Promise((resolve) => {
      const transaction = dbRef.current!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(filename)

      request.onsuccess = () => resolve()
      request.onerror = () => resolve()
    })
  }, [])

  /**
   * Clear all cached images
   */
  const clearCache = useCallback(async (): Promise<void> => {
    // Revoke all object URLs
    urlCache.current.forEach(url => URL.revokeObjectURL(url))
    urlCache.current.clear()

    if (!dbRef.current) return

    return new Promise((resolve) => {
      const transaction = dbRef.current!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => {
        console.log('[ImageCache] Cache cleared')
        resolve()
      }
      request.onerror = () => resolve()
    })
  }, [])

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(async (): Promise<{ count: number; totalSize: number }> => {
    if (!dbRef.current) return { count: 0, totalSize: 0 }

    return new Promise((resolve) => {
      const transaction = dbRef.current!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()

      request.onsuccess = () => {
        const entries = request.result as CacheEntry[]
        const totalSize = entries.reduce((sum, entry) => sum + entry.data.byteLength, 0)
        resolve({ count: entries.length, totalSize })
      }

      request.onerror = () => resolve({ count: 0, totalSize: 0 })
    })
  }, [])

  return {
    isReady,
    hasImage,
    getImageUrl,
    getImageData,
    setImage,
    removeImage,
    clearCache,
    getCacheStats,
  }
}
